import { cn } from '@/lib/cn.ts';
import {
  RESERVATION_SOURCES,
  RESERVATION_SOURCE_LABELS_TR,
  type ReservationSource,
  type ReservationStatus,
} from '@okn/shared';
import {
  ChevronLeft,
  ChevronRight,
  Download,
  FileText,
  Pencil,
  Plus,
  Search,
  Ticket,
} from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { SeatEditor } from './SeatEditor.tsx';
import { adminApi, downloadAdminFile, getAdminSession } from './auth.ts';
import { fmtDateTime } from './lib/format.ts';
import type { ListResponse, ReservationDetailData, ReservationListItem } from './lib/types.ts';
import { useDebounce } from './lib/useDebounce.ts';
import { StatusBadge } from './ui/Badge.tsx';
import { Button } from './ui/Button.tsx';
import { Card } from './ui/Card.tsx';
import { EmptyState } from './ui/EmptyState.tsx';
import { Select } from './ui/Field.tsx';
import { PageHeader } from './ui/PageHeader.tsx';
import { SkeletonRows } from './ui/Skeleton.tsx';
import { useToast } from './ui/Toast.tsx';

const STATUS_TABS: Array<{ value: ReservationStatus | ''; label: string }> = [
  { value: '', label: 'Tümü' },
  { value: 'reserved', label: 'Ön Rezervasyon' },
  { value: 'paid', label: 'Onaylandı' },
  { value: 'cancelled', label: 'İptal' },
  { value: 'no_show', label: 'Gelmedi' },
];

export function ReservationsList() {
  const [items, setItems] = useState<ReservationListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const pageSize = 25;
  const [q, setQ] = useState('');
  const [status, setStatus] = useState<ReservationStatus | ''>('');
  const [source, setSource] = useState<ReservationSource | ''>('');
  const [loading, setLoading] = useState(true);
  const [invitationBusyId, setInvitationBusyId] = useState<string | null>(null);
  const [editTarget, setEditTarget] = useState<{
    id: string;
    referenceCode: string;
    fullName: string;
    status: string;
    seats: Array<{ id: string; label: string; level: string }>;
  } | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editLoadingId, setEditLoadingId] = useState<string | null>(null);
  const debouncedQ = useDebounce(q.trim(), 250);
  const toast = useToast();

  // Open the seat editor straight from the row: the list item has no seat list,
  // so fetch the detail first to seed the editor.
  const openEdit = async (id: string) => {
    setEditLoadingId(id);
    try {
      const d = await adminApi<ReservationDetailData>(`/admin/reservations/${id}`);
      setEditTarget({
        id: d.reservation.id,
        referenceCode: d.reservation.referenceCode,
        fullName: d.reservation.fullName,
        status: d.reservation.status,
        seats: d.seats,
      });
      setEditorOpen(true);
    } catch {
      toast.error('Rezervasyon açılamadı.');
    } finally {
      setEditLoadingId(null);
    }
  };
  const closeEditor = () => {
    setEditorOpen(false);
    setTimeout(() => setEditTarget(null), 220);
  };

  const downloadInvitation = async (id: string) => {
    setInvitationBusyId(id);
    try {
      const { pdfUrl, htmlUrl } = await adminApi<{ pdfUrl: string | null; htmlUrl: string }>(
        `/admin/reservations/${id}/invitation`,
        { method: 'POST', body: {} },
      );
      window.open(pdfUrl ?? htmlUrl, '_blank', 'noopener');
    } catch {
      toast.error('Davet kartı oluşturulamadı.');
    } finally {
      setInvitationBusyId(null);
    }
  };

  const refresh = useCallback(async () => {
    setLoading(true);
    const qs = new URLSearchParams();
    qs.set('page', String(page));
    qs.set('pageSize', String(pageSize));
    if (debouncedQ) qs.set('q', debouncedQ);
    if (status) qs.set('status', status);
    if (source) qs.set('source', source);
    try {
      const data = await adminApi<ListResponse>(`/admin/reservations?${qs.toString()}`);
      setItems(data.items);
      setTotal(data.total);
    } finally {
      setLoading(false);
    }
  }, [page, debouncedQ, status, source]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Reset to first page whenever filters change.
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional page reset
  useEffect(() => setPage(1), [debouncedQ, status, source]);

  const exportCsv = () => {
    const s = getAdminSession();
    if (!s) return;
    const qs = new URLSearchParams();
    if (debouncedQ) qs.set('q', debouncedQ);
    if (status) qs.set('status', status);
    if (source) qs.set('source', source);
    fetch(`${import.meta.env.VITE_API_BASE_URL ?? ''}/admin/export.csv?${qs.toString()}`, {
      headers: { Authorization: `Bearer ${s.token}` },
    })
      .then((r) => r.blob())
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `rezervasyonlar-${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
        URL.revokeObjectURL(url);
      });
  };

  // Printable door check-in list (paid parties, seats with tick boxes + QR).
  const [rosterBusy, setRosterBusy] = useState(false);
  const downloadRoster = async () => {
    setRosterBusy(true);
    try {
      await downloadAdminFile('/admin/roster.pdf', 'salon-giris-listesi.pdf');
    } catch {
      toast.error('Liste oluşturulamadı.');
    } finally {
      setRosterBusy(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="mx-auto max-w-6xl">
      <PageHeader
        kicker="Yönetim"
        title="Rezervasyonlar"
        description={`${total} kayıt`}
        actions={
          <>
            <Button
              variant="secondary"
              size="sm"
              icon={<FileText className="h-4 w-4" />}
              onClick={downloadRoster}
              loading={rosterBusy}
              disabled={rosterBusy}
            >
              Giriş Listesi
            </Button>
            <Button
              variant="secondary"
              size="sm"
              icon={<Download className="h-4 w-4" />}
              onClick={exportCsv}
            >
              CSV
            </Button>
            <Link to="/admin/rezervasyonlar/yeni">
              <Button size="sm" icon={<Plus className="h-4 w-4" />}>
                Manuel
              </Button>
            </Link>
          </>
        }
      />

      {/* Filters */}
      <div className="mt-6 flex flex-col gap-3 lg:flex-row lg:items-center">
        <div className="relative flex-1">
          <Search className="-translate-y-1/2 pointer-events-none absolute top-1/2 left-3.5 h-4 w-4 text-text-faint" />
          <input
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Ad, e-posta, telefon veya referans ara…"
            className="input h-10 pl-10"
            inputMode="search"
          />
        </div>
        <Select
          value={source}
          onChange={(e) => setSource(e.target.value as ReservationSource | '')}
          className="h-10 lg:max-w-[160px]"
        >
          <option value="">Tüm kaynaklar</option>
          {RESERVATION_SOURCES.map((s) => (
            <option key={s} value={s}>
              {RESERVATION_SOURCE_LABELS_TR[s]}
            </option>
          ))}
        </Select>
      </div>

      {/* Segmented status filter */}
      <div className="mt-3 inline-flex flex-wrap gap-1 rounded-lg border border-border bg-bg-elevated p-1">
        {STATUS_TABS.map((tab) => (
          <button
            type="button"
            key={tab.value || 'all'}
            onClick={() => setStatus(tab.value)}
            className={cn(
              'rounded-md px-3 py-1.5 text-xs font-medium transition-colors',
              status === tab.value
                ? 'bg-accent-quiet text-text'
                : 'text-text-muted hover:text-text',
            )}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Table */}
      <Card className="mt-4 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px] text-sm">
            <thead className="sticky top-0 z-10 bg-bg-elevated text-[10px] uppercase tracking-wider text-text-faint">
              <tr className="border-b border-border">
                <th className="px-4 py-2.5 text-left font-normal">Referans</th>
                <th className="px-4 py-2.5 text-left font-normal">Misafir</th>
                <th className="px-4 py-2.5 text-left font-normal">Kaynak</th>
                <th className="px-4 py-2.5 text-right font-normal">Koltuk</th>
                <th className="px-4 py-2.5 text-left font-normal">Durum</th>
                <th className="px-4 py-2.5 text-right font-normal">Tarih</th>
                <th className="px-4 py-2.5 text-right font-normal">İşlem</th>
              </tr>
            </thead>
            {!loading && (
              <tbody className="divide-y divide-border">
                {items.map((r) => (
                  <tr key={r.id} className="group transition-colors hover:bg-surface">
                    <td className="px-4 py-2.5">
                      <Link
                        to={`/admin/rezervasyonlar/${r.id}`}
                        className="mono text-accent tnum hover:underline"
                      >
                        {r.referenceCode}
                      </Link>
                    </td>
                    <td className="px-4 py-2.5">
                      <div className="text-text">{r.fullName}</div>
                      <div className="mono text-[11px] text-text-faint tnum">{r.phone}</div>
                    </td>
                    <td className="px-4 py-2.5 text-text-muted">
                      {RESERVATION_SOURCE_LABELS_TR[r.source]}
                    </td>
                    <td className="px-4 py-2.5 text-right tnum">{r.seatCount}</td>
                    <td className="px-4 py-2.5">
                      <StatusBadge status={r.status} />
                    </td>
                    <td className="px-4 py-2.5 text-right text-[11px] text-text-faint tnum">
                      {fmtDateTime(r.createdAt)}
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      <div className="flex items-center justify-end gap-1.5">
                        {r.status !== 'cancelled' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            icon={<Pencil className="h-3.5 w-3.5" />}
                            disabled={editLoadingId === r.id}
                            onClick={() => openEdit(r.id)}
                          >
                            {editLoadingId === r.id ? '…' : 'Düzenle'}
                          </Button>
                        )}
                        {r.status === 'paid' && (
                          <Button
                            variant="secondary"
                            size="sm"
                            icon={<Ticket className="h-3.5 w-3.5" />}
                            disabled={invitationBusyId === r.id}
                            onClick={() => downloadInvitation(r.id)}
                          >
                            {invitationBusyId === r.id ? '…' : 'Davet'}
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            )}
          </table>
          {loading && <SkeletonRows rows={8} />}
          {!loading && items.length === 0 && (
            <EmptyState
              icon={<Search className="h-6 w-6" />}
              title="Kayıt bulunamadı"
              description="Filtreleri temizleyin ya da farklı bir arama terimi deneyin."
            />
          )}
        </div>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-4 flex items-center justify-between text-xs text-text-muted">
          <span className="tnum">
            Sayfa {page} / {totalPages}
          </span>
          <div className="flex gap-2">
            <Button
              variant="secondary"
              size="sm"
              icon={<ChevronLeft className="h-4 w-4" />}
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Önceki
            </Button>
            <Button
              variant="secondary"
              size="sm"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              Sonraki
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {editTarget && (
        <SeatEditor
          open={editorOpen}
          reservation={editTarget}
          onClose={closeEditor}
          onSaved={refresh}
        />
      )}
    </div>
  );
}
